<template>
  <div v-loading="loading" class="campaign-stats-overall-chart">
    <header class="campaign-stats-overall-chart__header">
      <div class="row align-middle">
        <div class="columns">
          <h3 class="campaign-stats-overall-chart__title">
            {{ $t('ADVERTISEMENT.CAMPAIGNS.BASIC_ADS_‌CHART_TITLE') }}
          </h3>
        </div>
        <div class="columns shrink">
          <RangeFilter :value="range" @input="$emit('update:range', $event)" />
        </div>
      </div>
    </header>

    <div class="campaign-stats-overall-chart__content">
      <div class="campaign-stats-overall-chart__legends">
        <CampaignStatsOverallChartLegend
          :title="$t('ADVERTISEMENT.CAMPAIGNS.BASIC.INSTALLS_COUNT')"
          :value="formatedInstallCount"
          class="campaign-stats-overall-chart__legend"
          :color="seriesColors[0]"
          :is-active="chartsVisibility[0]"
          @click="handleClickLegend(0)"
        />
        <CampaignStatsOverallChartLegend
          :title="$t('ADVERTISEMENT.BASIC_ADS.STATS.PAYMENT_PER_INSTALL')"
          :value="formattedCostPerInstall"
          class="campaign-stats-overall-chart__legend"
          :color="seriesColors[1]"
          :is-active="chartsVisibility[1]"
          @click="handleClickLegend(1)"
        />
      </div>

      <CampaignStatsOverallChartContent
        :stats="stats"
        :charts-visibility="chartsVisibility"
        :series-colors="seriesColors"
      />
    </div>
  </div>
</template>

<script>
import { BasicAdsCampaigns } from '@/api/api';
import { AbortedError } from '@/api/shared/transformers/errorTransformer';
import RangeFilter from '@@/common/RangeFilter';
import Color from '@/utils/color';
import { format } from '@/utils/number';
import campaignId from '@/mixins/advertisement/campaignId';
import CampaignStatsOverallChartLegend from './CampaignStatsOverallChartLegend';
import CampaignStatsOverallChartContent from './CampaignStatsOverallChartContent';

const SERIES_COUNT = 2;

export default {
  name: 'CampaignStatsOverallChart',

  components: {
    RangeFilter,
    CampaignStatsOverallChartLegend,
    CampaignStatsOverallChartContent,
  },

  mixins: [campaignId],

  props: {
    range: {
      type: String,
      required: true,
    },
  },

  data() {
    const chartsVisibility = {};
    for (let i = 0; i < SERIES_COUNT; i++) {
      chartsVisibility[i] = true;
    }

    return {
      rangeFilter: 'last-week',
      chartsVisibility,
      loading: false,
      stats: {},
    };
  },

  computed: {
    seriesColors() {
      return Color.get(SERIES_COUNT).byStep(2, 6);
    },
    formatedInstallCount() {
      return format(this.stats.installCount || 0);
    },
    formattedCostPerInstall() {
      return format(this.stats.costPerInstall || 0);
    },
  },

  watch: {
    range() {
      this.getCampaignData();
    },
  },

  mounted() {
    this.getCampaignData();
  },

  methods: {
    getCampaignData() {
      this.loading = true;

      return BasicAdsCampaigns.getCampaignStats(this.campaignId, this.range)
        .then(stats => {
          this.stats = stats;
        })
        .catch(error => {
          if (error instanceof AbortedError) return;
          const { message } = error;
          this.$message.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleClickLegend(index) {
      this.chartsVisibility[index] = !this.chartsVisibility[index];
    },
  },
};
</script>

<style lang="scss">
.campaign-stats-overall-chart {
  height: 100%;
  border: 1px solid $light-gray;
  border-radius: $lb-border-radius * 5;
  padding: $global-padding * 1.5;
  background-color: #fff;

  &__header {
    padding-bottom: $global-padding * 1.5;
    border-bottom: 1px solid $light-gray;
  }

  &__title {
    font-size: rem-calc(14px);
    font-weight: 700;
    margin-bottom: 0;
  }

  &__legends {
    padding-top: $global-padding;
    padding-bottom: $global-padding / 2;
    margin-bottom: $global-margin;
    display: flex;
  }

  &__legend {
    &:not(:first-child) {
      margin-left: calc($global-margin / 2);
    }
    @include rtl() {
      margin-left: 0;

      &:not(:last-child) {
        margin-left: calc($global-margin / 2);
      }
    }
  }
}
</style>
